import Modal from "src/components/common/Modal";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import {
    TextFilterField,
    CheckboxFilterField,
    SelectFilterField,
    RangeFilterField,
    AsyncSelectFilterField,
} from "./FilterField";
import { json } from "../types";
type FieldType = "text" | "checkbox" | "select" | "range" | "asyncSelect";
interface BaseFieldConfig {
    key: string;
    label: string;
    type: FieldType;
    default: string;
}
interface TextFieldConfig extends BaseFieldConfig {
    type: "text";
    transform?: (value: string) => string;
}
interface CheckboxFieldConfig extends BaseFieldConfig {
    type: "checkbox";
}
interface SelectFieldConfig extends BaseFieldConfig {
    type: "select";
    options: { value: string; label: string }[];
}
interface AsyncSelectFieldConfig extends BaseFieldConfig {
    type: "asyncSelect";
    fileName: string;
    placeholder?: string;
    isMulti?: boolean;
    formValues?: json;
}
interface RangeFieldConfig extends BaseFieldConfig {
    type: "range";
    inputType?: "date" | "number" | "text";
}

export type FilterFieldConfig =
    | TextFieldConfig
    | CheckboxFieldConfig
    | SelectFieldConfig
    | RangeFieldConfig
    | AsyncSelectFieldConfig;

type FilterState = Record<string, string>;

interface FilterModalProps {
    title?: string;
    width?: number;
    fields: FilterFieldConfig[];
}
export function buildDefaultState(fields: FilterFieldConfig[]): FilterState {
    const state: FilterState = {};
    for (const field of fields) {
        if (field.type === "range") {
        state[`${field.key}From`] = field.default;
        state[`${field.key}To`]   = field.default;
        } else if (field.type === "asyncSelect") {
        state[field.key]          = field.default;
        state[field.key + "Name"] = "";
        } else {
        state[field.key] = field.default;
        }
    }
    return state;
}
function readStateFromParams(
    fields: FilterFieldConfig[],
    searchParams: URLSearchParams
): FilterState {
    const state: FilterState = {};
    for (const field of fields) {
        if (field.type === "range") {
        state[`${field.key}From`] = searchParams.get(`${field.key}From`) ?? field.default;
        state[`${field.key}To`]   = searchParams.get(`${field.key}To`)   ?? field.default;
        } else if (field.type === "asyncSelect") {
        state[field.key]      = searchParams.get(field.key)      ?? field.default;
        state[field.key + "Name"] = searchParams.get(field.key + "Name") ?? "";
        } else {
        state[field.key] = searchParams.get(field.key) ?? field.default;
        }
    }
    return state;
}
export default function FilterModal({ title = "Filter", width = 650, fields }: FilterModalProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const fieldsRef = useRef<HTMLDivElement | null>(null);
    const defaultState = buildDefaultState(fields);
    const [draft, setDraft] = useState<FilterState>(() => readStateFromParams(fields, searchParams));
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
        const firstField = fieldsRef.current?.querySelector<HTMLElement>(
            "input:not([type='hidden']):not([disabled]), select:not([disabled]), textarea:not([disabled])"
        );
        firstField?.focus();
        }, 0);

        return () => window.clearTimeout(timeoutId);
    }, []);
    const setField = (key: string, value: string) =>
    setDraft(prev => ({ ...prev, [key]: value }));
    const handleSearch = () => {
            const params = new URLSearchParams();
            Object.entries(draft).forEach(([key, value]) => {
                if (value !== defaultState[key]) params.set(key, value);
            });
            params.set("page", "1");
            navigate({ pathname: "..", search: params.toString() }, { replace: true })   
    }
    return (
        <Modal
            closeIt={() => navigate({ pathname: "..", search: location.search }, { replace: true })}
            title={title}
            width={width}
            maxWidth="100%"
            onEnter={handleSearch}
        >
        <div ref={fieldsRef}>
            {fields.map(field => {
                if(field.type === "checkbox"){
                    return (
                        <CheckboxFilterField
                            key={field.key}
                            id={field.key}
                            label={field.label}
                            value={draft[field.key]}
                            onChange={v => setField(field.key, v)}
                        />
                    );
                }
                if(field.type === "select"){
                    return (
                        <SelectFilterField
                            key={field.key}
                            id={field.key}
                            label={field.label}
                            value={draft[field.key]}
                            options={field.options}
                            defaultValue={field.default}
                            onChange={v => setField(field.key, v)}
                        />
                    );
                }
                if(field.type === "range"){
                    return (
                        <RangeFilterField
                            key={field.key}
                            id={field.key}
                            label={field.label}
                            inputType={field.inputType}
                            valueFrom={draft[`${field.key}From`]}
                            valueTo={draft[`${field.key}To`]}
                            onChangeFrom={v => setField(`${field.key}From`, v)}
                            onChangeTo={v => setField(`${field.key}To`, v)}
                        />
                    );
                }
                if(field.type === "asyncSelect"){
                    return (
                        <AsyncSelectFilterField
                            key={field.key}
                            id={field.key}
                            label={field.label}
                            fileName={field.fileName}
                            placeholder={field.placeholder}
                            isMulti={field.isMulti}
                            valueId={draft[field.key]}
                            valueName={draft[field.key + "Name"]}
                            onChangeId={v => setField(field.key, v)}
                            onChangeName={v => setField(field.key + "Name", v)}
                            formValues = {field.formValues}
                        />
                    );
                }
                return (
                    <TextFilterField
                        key={field.key}
                        id={field.key}
                        label={field.label}
                        value={draft[field.key]}
                        onChange={v => setField(field.key, v)}
                        transform={(field as TextFieldConfig).transform}
                    />
                );
            })}
        </div>
            <div className="mt-5 mb-3">
                <button
                    children = "Clear Filter"
                    type = "button"
                    className = "btn btn-secondary"
                    onClick = {() => setDraft(defaultState)}
                />
                <button
                    children = "Search"
                    type = "button"
                    className = "btn btn-primary ms-3"
                    onClick = {handleSearch}
                />
            </div>
        </Modal>
    );
}
